import React, { useId } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Tooltip, Typography } from '@mui/material';

import { type ComputedReading } from '../readings/computedReading';

import { wandTargetAttribute } from './wandGeometry';

/** Keep a chart's arithmetic description available without competing with its plot. */
export default function LensChartDescription({ cardTitle, reading }: { cardTitle: string; reading: ComputedReading }) {
  const descriptionId = useId();
  const text = reading.kind === 'refused' ? reading.reason : reading.text;
  const description = (
    <Typography
      component="p"
      data-lens-wand={wandTargetAttribute({ kind: 'text', label: `${cardTitle} commentary`, value: text })}
      id={descriptionId}
      sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums', lineHeight: 1.5, m: 0, textAlign: 'left' }}
      variant="caption"
    >
      {text}
    </Typography>
  );

  // A limitation must stay visible rather than being hidden behind the disclosure.
  if (reading.kind === 'refused') {
    return <Box data-lens-card-description="">{description}</Box>;
  }

  return (
    <Box
      component="details"
      data-lens-card-description=""
      sx={{
        '&[open] .lens-chart-description-chevron': { transform: 'rotate(180deg)' },
        '&[open] > p': { maxHeight: 140, mt: 0.5, mb: 3, overflowY: 'auto' },
      }}
    >
      <Tooltip
        describeChild
        enterDelay={300}
        placement="top"
        slotProps={{ tooltip: { sx: { fontSize: 11, px: 0.75, py: 0.25 } } }}
        title="Description"
      >
        <Box
          aria-controls={descriptionId}
          aria-label={`Chart description for ${cardTitle}`}
          component="summary"
          sx={{
            alignItems: 'center',
            borderRadius: 0.5,
            color: 'text.secondary',
            cursor: 'pointer',
            display: 'inline-flex',
            height: 24,
            justifyContent: 'center',
            left: 4,
            listStyle: 'none',
            bottom: 0,
            opacity: 0,
            pointerEvents: 'none',
            position: 'absolute',
            width: 24,
            zIndex: 4,
            '&::-webkit-details-marker': { display: 'none' },
            '.MuiCard-root:hover &, &:focus-visible, details[open] > &': {
              opacity: 1,
              pointerEvents: 'auto',
            },
            '@media (hover: none), (pointer: coarse)': { opacity: 1, pointerEvents: 'auto' },
            '&:hover': { color: 'text.primary' },
            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
          }}
        >
          <ExpandMoreIcon aria-hidden="true" className="lens-chart-description-chevron" sx={{ fontSize: 16 }} />
        </Box>
      </Tooltip>
      {description}
    </Box>
  );
}
